import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import FieldsContainer from '../recordDetail/fields/FieldsContainer';
import DropdownField from '../recordDetail/fields/DropdownField';
import DateTimeField from '../recordDetail/fields/DateTimeField';
import TextField from '../recordDetail/fields/TextField';
import TermField from '../recordDetail/fields/TermField';
import { validateAllFields, FIELD_TYPE } from '../utility/validators';
import { extractNumber, addCommasToNum } from '../utility/fieldFormatters';

import icons from '../assets/icons';
import '../recordDetail/fields/ValidatableField.css';

import { toUI } from '../utility/mapStaticData';

import * as isinDetailActions from './reducer';
import * as staticDataActions from '../staticData/reducer';

import './IsinDetail.css';

export class IsinDetailComponent extends Component {
  static get propTypes() {
    return {
      editing: PropTypes.bool,
      fetching: PropTypes.bool,
      product: PropTypes.object,
      fields: PropTypes.arrayOf(PropTypes.shape({
        ProductFieldName: PropTypes.string.isRequired,
        Mandatory: PropTypes.bool
      })).isRequired,
      fetchingFields: PropTypes.bool,
      clipboardDate: PropTypes.any,
      staticData: PropTypes.object,
      onUpdateProperty: PropTypes.func.isRequired,
      onFetchStaticData: PropTypes.func.isRequired,
      onFetchProductData: PropTypes.func.isRequired,
      onFetchFields: PropTypes.func.isRequired,
      onSetCurrentTime: PropTypes.func,
      doValidateProxy: PropTypes.object,
      onCopyDate: PropTypes.func,
      onDeriveTermOfContract: PropTypes.func.isRequired
    };
  }

  constructor(props) {
    super(props);
    const { product, onFetchStaticData } = props;
    this.fields = {};

    // Fetch the static data initially, can later be done only upon entering edit mode / creating record
    onFetchStaticData();

    this.fetchFields(product);
  }

  componentWillReceiveProps(props) {
    const { product, doValidateProxy } = props;
    this.fetchFields(product);

    if (doValidateProxy) {
      doValidateProxy.validate = () => this.validate();
    }
  }

  fetchFields(product) {
    const { onFetchFields, onFetchProductData } = this.props;
    if (product) {
      onFetchFields(product.InstrumentTypeId).then(() => {
        onFetchProductData(product);
      });
    }
  }

  show(fieldName) {
    const { fields } = this.props;
    return !!fields.find(f => f.ProductFieldName === fieldName);
  }

  showDeriveButton() {
    const { editing, product } = this.props;
    return editing && product.TermofContractStartDate && product.ExpiryDate;
  }

  required(fieldName) {
    const { fields } = this.props;
    const field = fields.find(f => f.ProductFieldName === fieldName);

    return field ? field.Mandatory : false;
  }

  validate() {
    return validateAllFields(this.fields);
  }


  validateTermStartDateExpiryDateRange() {
    const { editing, product } = this.props;

    if (!editing || !product || !product.ExpiryDate || !product.TermofContractStartDate) {
      return null;
    }

    let message = null;

    const endDate = moment(product.ExpiryDate, 'YYYY-MM-DD');
    const startDate = moment(product.TermofContractStartDate, 'YYYY-MM-DD');

    if (!endDate.isValid() || !startDate.isValid()) {
      return null;
    }

    if (!endDate.isAfter(startDate, 'day')) {
      message = 'Date must be before the Expiry Date to Derive the Term of Contract!';
    } else {
      return null;
    }

    return (
      <div className="ValidatableField-message.error">
        <span>{message}</span>
        <img src={icons.red.warning} alt="warning" />
      </div>
    );
  }

  deriveTermOfContract(event) {
    const { onDeriveTermOfContract, product } = this.props;
    event.preventDefault();

    onDeriveTermOfContract(product.TermofContractStartDate, product.ExpiryDate);
  }

  render() {
    const { fetching, product, editing, fetchingFields, staticData,
      onUpdateProperty, onSetCurrentTime,
      clipboardDate, onCopyDate } = this.props;

    const isFxSwap = product !== null && product !== undefined && staticData.instrumentTypes.find(i => i.InstrumentTypeId === product.InstrumentTypeId && i.Name.endsWith('FX Swap')) !== undefined;

    if (fetching || fetchingFields || !product) {
      return (
        <div className="IsinDetail-empty">Loading product details</div>
      );
    }

    const errorMsg = this.validateTermStartDateExpiryDateRange();

    return (<div className="IsinDetail">
      <FieldsContainer>
        <DropdownField
          name="instrument-type"
          label="Instrument Type"
          editing={editing}
          required
          ref={(instance) => { this.fields.instrumentType = instance; }}
          value={{ InstrumentTypeId: product.InstrumentTypeId }}
          possibleValues={staticData.instrumentTypes}
          frequent={staticData.instrumentTypes.frequent}
          mapFn={toUI.instrumentType}
          onChange={updatedValue => onUpdateProperty('InstrumentTypeId', updatedValue && updatedValue.InstrumentTypeId)}
          toolTip="Instrument type using ISDA taxonomy"
        />

        {this.show('Isin') && <TextField
          name="isin"
          label="ISIN"
          editing={editing}
          value={product.Isin}
          required={this.required('Isin')}
          onChange={updatedValue => onUpdateProperty('Isin', updatedValue)}
          toolTip="Product ISIN"
        />}

        {this.show('SubProduct') && <DropdownField
          name="commodity-shortcuts"
          label="Commodity Shortcuts"
          editing={editing}
          required={false}
          ref={(instance) => { this.fields.commodityShortcut = instance; }}
          value={product.CommodityShortcut}
          possibleValues={staticData.commodityShortcuts}
          frequent={staticData.commodityShortcuts.frequent}
          mapFn={toUI.commodityShortcut}
          onChange={updatedValue => onUpdateProperty('CommodityShortcut', updatedValue)}
        />}

        {this.show('BaseProduct') && <DropdownField
          name="base-product"
          label="Base Product"
          editing={editing}
          required={this.required('BaseProduct')}
          ref={(instance) => { this.fields.baseProduct = instance; }}
          value={product.BaseProduct}
          possibleValues={staticData.baseProducts}
          frequent={staticData.baseProducts.frequent}
          mapFn={toUI.baseProduct}
          onChange={updatedValue => onUpdateProperty('BaseProduct', updatedValue)}
        />}

        {this.show('SubProduct') && <DropdownField
          name="sub-product"
          label="Sub Product"
          editing={editing}
          required={this.required('SubProduct') || staticData.subProducts.length > 0}
          ref={(instance) => { this.fields.subProduct = instance; }}
          value={product.SubProduct}
          possibleValues={staticData.subProducts}
          frequent={staticData.subProducts.frequent}
          mapFn={toUI.subProduct}
          onChange={updatedValue => onUpdateProperty('SubProduct', updatedValue)}
          validationMessage="A Sub Product is required"
        />}

        {this.show('AdditionalSubProduct') && <DropdownField
          name="additional-sub-product"
          label="Additional Sub Product"
          editing={editing}
          required={(this.required('AdditionalSubProduct') || staticData.additionalSubProducts.length > 0)}
          ref={(instance) => { this.fields.additionalSubProduct = instance; }}
          value={product.AdditionalSubProduct}
          possibleValues={staticData.additionalSubProducts}
          frequent={staticData.additionalSubProducts.frequent}
          mapFn={toUI.subProduct}
          onChange={updatedValue => onUpdateProperty('AdditionalSubProduct', updatedValue)}
          validationMessage="An Additional Sub Product is required"
        />}

        {this.show('BaseProduct2') && <DropdownField
          name="base-product-2"
          label="Other Base Product"
          editing={editing}
          required={this.required('BaseProduct2')}
          ref={(instance) => { this.fields.baseProduct2 = instance; }}
          value={product.BaseProduct2}
          possibleValues={staticData.baseProduct2s}
          frequent={staticData.baseProduct2s.frequent}
          mapFn={toUI.baseProduct}
          onChange={updatedValue => onUpdateProperty('BaseProduct2', updatedValue)}
        />}

        {this.show('OtherSubProduct') && <DropdownField
          name="other-sub-product"
          label="Other Sub Product"
          editing={editing}
          required={this.required('OtherSubProduct')}
          ref={(instance) => { this.fields.otherSubProduct = instance; }}
          value={product.OtherSubProduct}
          possibleValues={staticData.otherSubProducts}
          frequent={staticData.otherSubProducts.frequent}
          mapFn={toUI.subProduct}
          onChange={updatedValue => onUpdateProperty('OtherSubProduct', updatedValue)}
        />}

        {this.show('OtherAdditionalSubProduct') && <DropdownField
          name="other-additional-sub-product"
          label="Other Additional Sub Product"
          editing={editing}
          required={this.required('OtherAdditionalSubProduct')}
          ref={(instance) => { this.fields.otherAdditionalSubProduct = instance; }}
          value={product.OtherAdditionalSubProduct}
          possibleValues={staticData.otherAdditionalSubProducts}
          frequent={staticData.otherAdditionalSubProducts.frequent}
          mapFn={toUI.subProduct}
          onChange={updatedValue => onUpdateProperty('OtherAdditionalSubProduct', updatedValue)}
        />}

        {this.show('DeliveryType') && <DropdownField
          name="delivery-type"
          label="Delivery Type"
          editing={editing}
          required={this.required('DeliveryType')}
          ref={(instance) => { this.fields.deliveryType = instance; }}
          value={product.DeliveryType}
          possibleValues={staticData.deliveryTypes}
          frequent={staticData.deliveryTypes.frequent}
          mapFn={toUI.deliveryType}
          onChange={updatedValue => onUpdateProperty('DeliveryType', updatedValue)}
        />}

        {this.show('FinalPriceType') && <DropdownField
          name="final-price-type"
          label="Final Price Type"
          editing={editing}
          required={this.required('FinalPriceType')}
          ref={(instance) => { this.fields.finalPriceType = instance; }}
          value={product.FinalPriceType}
          possibleValues={staticData.finalPriceTypes}
          frequent={staticData.finalPriceTypes.frequent}
          mapFn={toUI.finalPriceType}
          onChange={updatedValue => onUpdateProperty('FinalPriceType', updatedValue)}
        />}

        <div className="FieldsContainer-group">
          {this.show('NotionalCcy') && <DropdownField
            name="notional-ccy"
            label={isFxSwap === true ? 'Base Currency' : 'Notional Currency'}
            editing={editing}
            required={this.required('NotionalCcy')}
            ref={(instance) => { this.fields.notionalCcy = instance; }}
            value={product.NotionalCcy}
            possibleValues={staticData.notionalCcys}
            frequent={staticData.notionalCcys.frequent}
            mapFn={toUI.currency}
            onChange={updatedValue => onUpdateProperty('NotionalCcy', updatedValue)}
          />}

          {this.show('ReferenceRate') && <DropdownField
            name="reference-rate"
            label="Reference Rate"
            editing={editing}
            required={this.required('ReferenceRate')}
            ref={(instance) => { this.fields.referenceRate = instance; }}
            value={product.ReferenceRate}
            possibleValues={staticData.referenceRates}
            frequent={staticData.referenceRates.frequent}
            mapFn={toUI.referenceRate}
            onChange={updatedValue => onUpdateProperty('ReferenceRate', updatedValue)}
          />}

        </div>

        {this.show('ReferenceRateTerm') && <TermField
          name="reference-rate-term"
          label="Reference Rate Term"
          unitLabel="Reference Rate Unit"
          editing={editing}
          required={this.required('ReferenceRateTerm')}
          ref={(instance) => { this.fields.referenceRateTerm = instance; }}
          onChange={updatedValue => onUpdateProperty('ReferenceRateTerm', updatedValue)}
          value={product.ReferenceRateTerm}
        />}

        {this.show('SpotDate') && <DateTimeField
          name="spot-date"
          label="Near Leg Expiry Date"
          editing={editing}
          required={this.required('SpotDate')}
          ref={(instance) => { this.fields.spotDate = instance; }}
          onChange={updatedValue => onUpdateProperty('SpotDate', updatedValue && updatedValue.Date)}
          time={false}
          timeZones={staticData.timeZones}
          value={{ Date: product.SpotDate }}
          onSetCurrentTime={timeZoneName => onSetCurrentTime('SpotDate', timeZoneName)}
          clipboardDate={clipboardDate}
          onCopy={onCopyDate}
        />}

        <div className="FieldsContainer-group">
          {this.show('NotionalCcy2') && <DropdownField
            name="notional-ccy-2"
            label={isFxSwap === true ? 'Reference Currency' : 'Other Notional Currency'}
            editing={editing}
            required={this.required('NotionalCcy2')}
            ref={(instance) => { this.fields.notionalCcy2 = instance; }}
            value={product.NotionalCcy2}
            possibleValues={staticData.notionalCcy2s}
            frequent={staticData.notionalCcy2s.frequent}
            mapFn={toUI.currency}
            onChange={updatedValue => onUpdateProperty('NotionalCcy2', updatedValue)}
          />}

          {this.show('ReferenceRate2') && <DropdownField
            name="reference-rate-2"
            label="Other Leg Reference Rate"
            editing={editing}
            required={this.required('ReferenceRate2')}
            ref={(instance) => { this.fields.referenceRate2 = instance; }}
            value={product.ReferenceRate2}
            possibleValues={staticData.referenceRate2s}
            frequent={staticData.referenceRate2s.frequent}
            mapFn={toUI.referenceRate}
            onChange={updatedValue => onUpdateProperty('ReferenceRate2', updatedValue)}
          />}
        </div>

        {this.show('ReferenceRateTerm2') && <TermField
          name="reference-rate-term-2"
          label="Other Leg Reference Rate Term"
          unitLabel="Other Leg Ref Rate Unit"
          editing={editing}
          required={this.required('ReferenceRateTerm2')}
          ref={(instance) => { this.fields.referenceRateTerm2 = instance; }}
          onChange={updatedValue => onUpdateProperty('ReferenceRateTerm2', updatedValue)}
          value={product.ReferenceRateTerm2}
        />}

        {this.show('NotionalSchedule') && <DropdownField
          name="notional-schedule"
          label="Notional Schedule"
          editing={editing}
          required={this.required('NotionalSchedule')}
          ref={(instance) => { this.fields.notionalSchedule = instance; }}
          value={product.NotionalSchedule}
          possibleValues={staticData.notionalSchedules}
          frequent={staticData.notionalSchedules.frequent}
          mapFn={toUI.notionalSchedule}
          onChange={updatedValue => onUpdateProperty('NotionalSchedule', updatedValue)}
        />}

        {this.show('ReturnorPayoutTrigger') && <DropdownField
          name="return-or-payout-trigger"
          label="Return or Payout Trigger"
          editing={editing}
          required={this.required('ReturnorPayoutTrigger')}
          ref={(instance) => { this.fields.returnorPayoutTrigger = instance; }}
          value={product.ReturnorPayoutTrigger}
          possibleValues={staticData.returnorPayoutTriggers}
          frequent={staticData.returnorPayoutTriggers.frequent}
          mapFn={toUI.returnorPayoutTrigger}
          onChange={updatedValue => onUpdateProperty('ReturnorPayoutTrigger', updatedValue)}
        />}

        {this.show('SettlementCurrency') && <DropdownField
          name="settlement-ccy"
          label="Settlement Currency"
          editing={editing}
          required={this.required('SettlementCurrency')}
          ref={(instance) => { this.fields.settlementCurrency = instance; }}
          value={product.SettlementCurrency}
          possibleValues={staticData.settlementCurrencies}
          frequent={staticData.settlementCurrencies.frequent}
          mapFn={toUI.currency}
          onChange={updatedValue => onUpdateProperty('SettlementCurrency', updatedValue)}
        />}

        {this.show('ExpiryDate') && <DateTimeField
          name="expiry-date"
          label={this.show('SpotDate') ? 'Far Leg Expiry Date' : 'Expiry Date'}
          editing={editing}
          required={this.required('ExpiryDate')}
          ref={(instance) => { this.fields.expiryDate = instance; }}
          onChange={updatedValue => onUpdateProperty('ExpiryDate', updatedValue && updatedValue.Date)}
          time={false}
          timeZones={staticData.timeZones}
          value={{ Date: product.ExpiryDate }}
          onSetCurrentTime={timeZoneName => onSetCurrentTime('ExpiryDate', timeZoneName)}
          clipboardDate={clipboardDate}
          onCopy={onCopyDate}
        />}

        {this.show('TransactionType') && <DropdownField
          name="transaction-type"
          label="Transaction Type"
          editing={editing}
          required={this.required('TransactionType')}
          ref={(instance) => { this.fields.transactionType = instance; }}
          value={product.TransactionType}
          possibleValues={staticData.transactionTypes}
          frequent={staticData.transactionTypes.frequent}
          mapFn={toUI.transactionType}
          onChange={updatedValue => onUpdateProperty('TransactionType', updatedValue)}
        />}

        {this.show('PriceMultiplier') && <TextField
          name="price-multiplier"
          label="Price Multiplier"
          ref={(instance) => { this.fields.priceMultiplier = instance; }}
          editing={editing}
          value={addCommasToNum(product.PriceMultiplier)}
          validationMessage="Please enter a valid number"
          inputType={FIELD_TYPE.COMMA_NUMERIC}
          required={this.required('PriceMultiplier')}
          onChange={updatedValue => onUpdateProperty('PriceMultiplier', extractNumber(updatedValue))}
        />}

        {this.show('PlaceofSettlement') && <DropdownField
          name="place-of-settlement"
          label="Place of Settlement"
          editing={editing}
          required={this.required('PlaceofSettlement')}
          ref={(instance) => { this.fields.placeofSettlement = instance; }}
          value={product.PlaceofSettlement}
          possibleValues={staticData.placesofSettlement}
          frequent={staticData.placesofSettlement.frequent}
          mapFn={toUI.placeofSettlement}
          onChange={updatedValue => onUpdateProperty('PlaceofSettlement', updatedValue)}
        />}

        {this.show('UnderlyingAssetType') && <DropdownField
          name="underlying-asset-type"
          label="Underlying Asset Type"
          editing={editing}
          required={this.required('UnderlyingAssetType')}
          ref={(instance) => { this.fields.underlyingAssetType = instance; }}
          value={product.UnderlyingAssetType}
          possibleValues={staticData.underlyingAssetTypes}
          frequent={staticData.underlyingAssetTypes.frequent}
          mapFn={toUI.underlyingAssetType}
          onChange={updatedValue => onUpdateProperty('UnderlyingAssetType', updatedValue)}
        />}

        {this.show('UnderlyingInstrumentIndex') && <DropdownField
          name="underlying-instrument-index"
          label="Underlying Instrument Index"
          editing={editing}
          required={this.required('UnderlyingInstrumentIndex')}
          ref={(instance) => { this.fields.underlyingInstrumentIndex = instance; }}
          value={product.UnderlyingInstrumentIndex}
          possibleValues={staticData.underlyingInstrumentIndicies}
          frequent={staticData.underlyingInstrumentIndicies.frequent}
          mapFn={toUI.underlyingInstrumentIndex}
          onChange={updatedValue => onUpdateProperty('UnderlyingInstrumentIndex', updatedValue)}
        />}

        {this.show('UnderlyingCreditIndexSeries') && <TextField
          name="underlying-instrument-credit-series"
          label="Underlying Instrument Credit Index Series"
          editing={editing}
          inputType="commaNumeric"
          validationMessage="Please enter a number between 1 and 999"
          required={this.required('UnderlyingCreditIndexSeries')}
          ref={(instance) => { this.fields.underlyingCreditIndexSeries = instance; }}
          onChange={updatedValue => onUpdateProperty('UnderlyingCreditIndexSeries', updatedValue)}
          value={product.UnderlyingCreditIndexSeries}
        />}

        {this.show('UnderlyingCreditIndexVersion') && <TextField
          name="underlying-instrument-credit-version"
          label="Underlying Instrument Credit Index Version"
          editing={editing}
          inputType="commaNumeric"
          validationMessage="Please enter a number between 1 and 999"
          required={this.required('UnderlyingCreditIndexVersion')}
          ref={(instance) => { this.fields.underlyingCreditIndexVersion = instance; }}
          onChange={updatedValue => onUpdateProperty('UnderlyingCreditIndexVersion', updatedValue)}
          value={product.UnderlyingCreditIndexVersion}
        />}

        {this.show('UnderlyingInstrumentProp') && <TextField
          name="underlying-instrument-prop"
          label="Underlying Instrument Prop"
          editing={editing}
          required={this.required('UnderlyingInstrumentProp')}
          ref={(instance) => { this.fields.underlyingInstrumentProp = instance; }}
          onChange={updatedValue => onUpdateProperty('UnderlyingInstrumentProp', updatedValue)}
          value={product.UnderlyingInstrumentProp}
        />}

        {this.show('UnderlyingInstrumentISIN') && <TextField
          name="underlying-instrument-isin"
          label="Underlying Instrument Isin"
          editing={editing}
          required={this.required('UnderlyingInstrumentISIN')}
          ref={(instance) => { this.fields.underlyingInstrumentIsin = instance; }}
          onChange={updatedValue => onUpdateProperty('UnderlyingInstrumentISIN', updatedValue)}
          value={product.UnderlyingInstrumentISIN}
        />}

        {this.show('UnderlyingInstrumentTerm') && <TermField
          name="underlying-instrument-term"
          label="Underlying Instrument Index Term"
          unitLabel="Underlying Inst Index Unit"
          editing={editing}
          ref={(instance) => { this.fields.underlyingInstrumentTerm = instance; }}
          required={this.required('UnderlyingInstrumentTerm')}
          onChange={updatedValue => onUpdateProperty('UnderlyingInstrumentTerm', updatedValue)}
          value={product.UnderlyingInstrumentTerm}
        />}

        {this.show('UnderlyingInstrumentLEI') && <TextField
          name="underlying-instrument-lei"
          label="Underlying Instrument LEI"
          editing={editing}
          required={this.required('UnderlyingInstrumentLEI')}
          ref={(instance) => { this.fields.underlyingInstrumentLEI = instance; }}
          onChange={updatedValue => onUpdateProperty('UnderlyingInstrumentLEI', updatedValue)}
          value={product.UnderlyingInstrumentLEI}
        />}

        {this.show('OptionType') && <DropdownField
          name="option-type"
          label="Option Type"
          editing={editing}
          required={this.required('OptionType')}
          ref={(instance) => { this.fields.optionType = instance; }}
          value={product.OptionType}
          possibleValues={staticData.optionTypes}
          frequent={staticData.optionTypes.frequent}
          mapFn={toUI.optionType}
          onChange={updatedValue => onUpdateProperty('OptionType', updatedValue)}
        />}

        {this.show('OptionExerciseStyle') && <DropdownField
          name="option-exercise-style"
          label="Option Exercise Style"
          editing={editing}
          required={this.required('OptionExerciseStyle')}
          ref={(instance) => { this.fields.optionExerciseStyle = instance; }}
          value={product.OptionExerciseStyle}
          possibleValues={staticData.optionExerciseStyles}
          frequent={staticData.optionExerciseStyles.frequent}
          mapFn={toUI.optionExerciseStyle}
          onChange={updatedValue => onUpdateProperty('OptionExerciseStyle', updatedValue)}
        />}

        {this.show('ValuationMethodorTrigger') && <DropdownField
          name="valuation-or-trigger-method"
          label="Valuation or Trigger Method"
          editing={editing}
          required={this.required('ValuationMethodorTrigger')}
          ref={(instance) => { this.fields.valuationMethodorTrigger = instance; }}
          value={product.ValuationMethodorTrigger}
          possibleValues={staticData.valuationMethodorTriggers}
          frequent={staticData.valuationMethodorTriggers.frequent}
          mapFn={toUI.valuationMethodorTrigger}
          onChange={updatedValue => onUpdateProperty('ValuationMethodorTrigger', updatedValue)}
        />}

        {this.show('TermofContractStartDate') && editing && <DateTimeField
          name="term-of-contract-start-date"
          label={'Term Start Date'}
          editing={editing}
          required={this.required('TermofContractStartDate')}
          ref={(instance) => { this.fields.termOfContractStartDate = instance; }}
          onChange={updatedValue => onUpdateProperty('TermofContractStartDate', updatedValue && updatedValue.Date)}
          time={false}
          timeZones={staticData.timeZones}
          value={{ Date: product.TermofContractStartDate }}
          onSetCurrentTime={timeZoneName => onSetCurrentTime('TermofContractStartDate', timeZoneName)}
          clipboardDate={clipboardDate}
          onCopy={onCopyDate}
          toolTip="Set the Date along with the Expiry Date to Derive the Term of Contract"
        />}

        { errorMsg && <span>{errorMsg}</span> }

        <span className="FieldsContainer-group">
          {this.show('TermofContractValue') && <TextField
            name="term-of-contract-value"
            label="Term of Contract Value"
            editing={editing}
            inputType="commaNumeric"
            validationMessage="Please enter a number between 1 and 999"
            required={this.required('TermofContractValue')}
            ref={(instance) => { this.fields.termofContractValue = instance; }}
            onChange={updatedValue => onUpdateProperty('TermofContractValue', updatedValue)}
            value={product.TermofContractValue}
          />}

          {this.show('TermofContractUnit') && <DropdownField
            name="term-of-contract-unit"
            label="Term of Contract Unit"
            editing={editing}
            required={this.required('TermofContractUnit')}
            ref={(instance) => { this.fields.termofContractUnit = instance; }}
            value={product.TermofContractUnit}
            possibleValues={staticData.termofContractUnits}
            mapFn={toUI.termofContractUnit}
            onChange={updatedValue => onUpdateProperty('TermofContractUnit', updatedValue)}
          />}

          { this.show('TermofContractStartDate') && !errorMsg && this.showDeriveButton() &&
            <button className="btn btn-primary-padtop" onClick={event => this.deriveTermOfContract(event)} title="Derive the Term from the Expiry and Start Dates">Derive</button>
          }
        </span>

      </FieldsContainer>
    </div>);
  }
}

const mapStateToProps = state => ({
  ...state.isinDetail,
  editing: state.recordDetail.editing,
  staticData: state.staticData,
});

const mapDispatchToProps = (dispatch) => {
  const {
    setProductProperty,
    fetchFieldsIfNeeded,
    setCurrentTime,
    fetchDerivedTermOfContract
  } = bindActionCreators(isinDetailActions, dispatch);
  const {
    fetchStaticDataIfNeeded,
    fetchProductStaticDataIfNeeded
  } = bindActionCreators(staticDataActions, dispatch);

  return {
    onUpdateProperty: setProductProperty,
    onFetchStaticData: fetchStaticDataIfNeeded,
    onFetchProductData: fetchProductStaticDataIfNeeded,
    onFetchFields: fetchFieldsIfNeeded,
    onSetCurrentTime: setCurrentTime,
    onDeriveTermOfContract: fetchDerivedTermOfContract
  };
};

const IsinDetail = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(IsinDetailComponent));
export default IsinDetail;



// WEBPACK FOOTER //
// ./src/isinDetail/IsinDetail.js